import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, PackagePlus, Phone, X } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";
import OpenNowBadge from "@/components/OpenNowBadge";

const LINKS = [
  { label: "Quote", hash: "#quote" },
  { label: "Services", hash: "#services" },
  { label: "Story", hash: "#story" },
  { label: "Coverage", hash: "#coverage" },
  { label: "Fleet", hash: "#fleet" },
  { label: "Reviews", hash: "#reviews" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToSection(hash);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "glass-bar border-b border-white/10" : "border-b border-transparent"
      }`}
    >
      <div className="relative mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <div className="flex items-center gap-8">
          <button
            data-testid="nav-logo-button"
            onClick={() => go("#top")}
            className="flex items-center gap-3"
          >
            <img
              src="/assets/logo.png"
              alt="The Kilted Courier logo"
              className="h-11 w-11 rounded-full bg-[#F6F1E3] object-contain p-0.5 ring-1 ring-[#F0A829]/50"
            />
            <span className="font-display hidden text-lg font-bold uppercase tracking-wide text-[#F6F1E3] lg:inline">
              The Kilted <span className="text-[#F0A829]">Courier</span>
            </span>
          </button>

          <nav className="hidden items-center gap-6 min-[1450px]:flex" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.hash}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.hash)}
                className="font-mono-brand text-[11px] uppercase tracking-[0.2em] text-[#B9C4B4] transition-colors duration-300 hover:text-[#F0A829]"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:block">
            <OpenNowBadge />
          </div>
          <a
            data-testid="nav-quote-button"
            href="https://kiltedcouriers.app"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-2 rounded-full bg-[#F0A829] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#0C1F17] transition-transform duration-300 hover:-translate-y-0.5 min-[820px]:flex lg:text-sm min-[1450px]:static min-[1450px]:translate-x-0"
          >
            <PackagePlus className="h-4 w-4" strokeWidth={2.5} />
            Get a Quote
          </a>
          <a
            data-testid="nav-call-button"
            href="tel:+61408464106"
            title="Call 0408 464 106"
            aria-label="Call 0408 464 106"
            className="hidden h-11 w-11 items-center justify-center rounded-full border border-[#F6F1E3]/25 text-[#F6F1E3] transition-colors duration-300 hover:border-[#F0A829] hover:text-[#F0A829] min-[820px]:flex lg:hidden"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
          </a>
          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen(!open)}
            className="rounded-full border border-white/15 p-2.5 text-[#F6F1E3] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={`mx-auto max-w-7xl px-5 pb-3 min-[820px]:hidden ${scrolled ? "hidden" : "block"}`}
        data-testid="nav-mobile-cta-bar"
      >
        <a
          data-testid="nav-mobile-bar-quote-button"
          href="https://kiltedcouriers.app"
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F0A829] px-4 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#0C1F17]"
        >
          <PackagePlus className="h-3.5 w-3.5" strokeWidth={2.5} />
          Get a Quote
        </a>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="glass-bar overflow-hidden border-b border-white/10 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.hash)}
                  className="rounded-lg px-3 py-3 text-left font-display text-2xl font-bold uppercase tracking-wide text-[#F6F1E3] transition-colors duration-300 hover:text-[#F0A829]"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-2 px-1">
                <OpenNowBadge className="justify-center" />
              </div>
              <a
                data-testid="nav-mobile-call-button"
                href="tel:+61408464106"
                className="mt-1 flex items-center justify-center gap-2 rounded-full border border-[#F6F1E3]/25 px-5 py-3 font-display text-base font-bold uppercase tracking-wider text-[#F6F1E3]"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Call 0408 464 106
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
